import PropTypes from 'prop-types'
import React, { Component, createRef } from 'react'
import ShippingOptionsForm from '../ShippingOptionsForm/ShippingOptionsForm'
import { Step } from '@ynap/multi-step-form'
import BEMHelper from '@ynap/bem-helper'
import { getCrisisNotification } from '../../utils/crisis'
import { getJITNotification } from '../../utils/jit'
import { MetaData } from '../../utils/getMetaData'
import createAnalyticsEvent from '../analytics'
import track from 'react-tracking'
import { SingleBadge } from '@ynap/badge'
const bem = new BEMHelper('ShippingOptionsStep')

@track()
class ShippingOptionsStep extends Component {
    static propTypes = {
        shippingOptions: PropTypes.array,
        path: PropTypes.string.isRequired,
        nextPath: PropTypes.string,
        locale: PropTypes.object.isRequired,
        setShippingMethod: PropTypes.func.isRequired,
        currentShippingOption: PropTypes.object,
        currentSignatureRequired: PropTypes.string,
        disabled: PropTypes.bool,
        completed: PropTypes.bool,
        onChange: PropTypes.func.isRequired,
        messages: PropTypes.object.isRequired,
        signatureNotification: PropTypes.bool,
        enableConsidered: PropTypes.bool,
        handleStepClick: PropTypes.func.isRequired,
    }

    constructor(props) {
        super(props)
        this.shippingOptionsRef = createRef()
    }

    componentDidMount() {
        const { shippingOptions, fetchCart } = this.props

        if (!shippingOptions) {
            fetchCart()
        }
    }

    componentDidUpdate(prevProps) {
        const { handleStepClick, hasShippingMethodChanged } = this.props

        if (prevProps.hasShippingMethodChanged !== hasShippingMethodChanged) {
            handleStepClick(!hasShippingMethodChanged)
        }
    }

    renderForm = (props) => {
        const {
            shippingOptions = [],
            jitShippingOptions = [],
            currentShippingOption,
            currentShippingOptionJit,
            currentOrderShippingInfo,
            currentOrderShippingInfoJit,
            hasJITItems,
            setShippingMethod,
            nextPath,
            paymentPath,
            messages,
            signatureNotification,
            locale,
            enableConsidered,
            crisisMessages,
            config,
            jitMessages,
            checkoutAnalytics,
            lineItems,
            isPostSplit,
            remoteItems,
            localItems,
            signatureRequired,
        } = props

        const currentOrderSignatureRequired = currentOrderShippingInfo?.currentSignatureRequired || currentOrderShippingInfoJit?.currentSignatureRequired
        return (
            <ShippingOptionsForm
                shippingOptions={shippingOptions}
                jitShippingOptions={jitShippingOptions}
                hasJITItems={hasJITItems}
                onSubmit={(values) => {
                    return setShippingMethod(
                        values,
                        nextPath,
                        currentOrderSignatureRequired,
                        isPostSplit,
                        lineItems,
                        [shippingOptions, jitShippingOptions],
                        paymentPath.replace(':locale', locale.id)
                    )
                }}
                initialValues={{
                    shippingOptionId: currentShippingOption?.id || shippingOptions?.[0]?.id,
                    shippingOptionIdJit: currentShippingOptionJit?.id || jitShippingOptions?.[0]?.id,
                    signatureRequired,
                    nominatedSlot: currentShippingOption?.defaultNominatedSlot,
                }}
                messages={messages}
                enableReinitialize
                keepDirtyOnReinitialize
                signatureNotification={signatureNotification}
                locale={locale}
                enableConsidered={enableConsidered}
                crisisMessages={crisisMessages}
                config={config}
                jitMessages={jitMessages}
                checkoutAnalytics={checkoutAnalytics}
                remoteItems={remoteItems}
                localItems={localItems}
                isPostSplit={isPostSplit}
            />
        )
    }

    renderSummary(props) {
        const {
            currentShippingOption,
            currentSignatureRequired,
            messages,
            getRequestedSlot,
            hasJITItems,
            enableConsidered,
            crisisMessages,
            jitMessages,
            isPostSplit,
        } = props

        const { name, nameEN, description, finalShippingPrice } = currentShippingOption

        const shippingSummary = description.map((line, index) => (
            <li key={index} className={bem('notBulleted')}>
                {line}
            </li>
        ))
        const namePrice = name ? <li className={bem('bulleted')}>{`${name} - ${finalShippingPrice}`}</li> : null

        const crisisMessage = getCrisisNotification(bem, false, crisisMessages)
        const jitMessage = hasJITItems && !isPostSplit ? getJITNotification(jitMessages, 'shippingStepA', bem) : null

        return (
            <>
                <ul className={bem('summary')} ref={this.shippingOptionsRef}>
                    {namePrice}
                    {shippingSummary}
                    {currentSignatureRequired === 'Y' 
                        ? <li className={bem('bulleted')}>{messages?.shippingOptionsStep.signatureRequired()}</li>
                        : messages?.shippingOptionsStep.noSignatureRequired?.() ? <li className={bem('bulleted')}>{messages?.shippingOptionsStep.noSignatureRequired()}</li> : null}
                    {getRequestedSlot ? <li>{getRequestedSlot(messages.shippingOptionsForm.relativeDay).label}</li> : null}
                    {enableConsidered && nameEN === 'Standard' ? (
                        <div className={bem('consideredWrapper')}>
                            <span className={bem('considered')}>
                                <SingleBadge
                                    className={bem('consideredBadge')}
                                    considered={true}
                                    badge={{ label: 'consideredBadge', key: 'CONSIDERED_BADGE' }}
                                    translation={messages.considered.badge()}
                                />
                            </span>
                            <span className={bem('consideredMessage')}>{messages.considered.consideredDelivery()}</span>
                        </div>
                    ) : null}
                </ul>
                {crisisMessage}
                {jitMessage}
            </>
        )
    }

    @track(({ checkoutAnalytics = {} }) =>
        createAnalyticsEvent({
            ...checkoutAnalytics,
            pageConfigs: {
                pageName: 'checkout - shipping options',
                pageType: 'checkout',
                primaryCategory: 'purchase',
                subCategory1: 'checkout',
                subCategory2: 'shipping options',
            },
        })
    )
    trackPage() {}

    render() {
        const { locale, messages, config, ...rest } = this.props
        const invisible = !this.props.location?.pathname?.includes('/checkout/shipping-options')

        return (
            <>
                {!invisible && <MetaData title={messages.shippingOptionsForm.title()} brandId={config.global.application.brandId} />}
                <Step
                    title={messages.shippingOptionsForm.title()}
                    form={this.renderForm}
                    summary={this.renderSummary.bind(this)}
                    locale={locale}
                    messages={messages}
                    pageAnalyticsStep="shipping options"
                    pageAnalyticsFn={this.trackPage.bind(this)}
                    invisible={invisible}
                    config={config}
                    isCheckoutLayout={true}
                    {...rest}
                />
            </>
        )
    }
}

export default ShippingOptionsStep
